import { useMemo, useState } from "react";
import { Button, ButtonWithLoading } from "../common/Button";
import { Modal } from "../common/Modal";
import { Text } from "../common/Text";
import { View } from "../common/View";
import { FormError } from "../common/FormError";
import { CategoryDefinitions, GroupType } from "loot-core/shared/onboarding";
import { CategoryGroupEntity } from "loot-core/types/models";
import CategoryGroupsAutocomplete from "../autocomplete/CategoryGroupsAutocomplete";
import { CommonModalProps } from "../Modals";

type CreateCategoryProps = {
  modalProps: Partial<CommonModalProps>;
  categoryGroups: Array<CategoryGroupEntity & { groupType: GroupType | null }>;
  catName: string,
  onCreate: (newGroup: CategoryGroupEntity & { groupType: GroupType }) => void,
  onDismiss: (isCategoryCreated: boolean) => void;
}

const CreateCategory: React.FC<CreateCategoryProps> = ({ catName, onCreate, categoryGroups, modalProps, onDismiss }) => {
  const [catGroupIsValid, setCatGroupIsValid] = useState(true);
  const [newCategoryGroup, setNewCategoryGroup] = useState<CategoryGroupEntity & { groupType: GroupType } | null>(null);

  const categoryGroupsFiltered = useMemo(() => {
    return categoryGroups.filter(g => g.groupType);
  }, [categoryGroups]);

  const modalCloseHandler = (isCreated = false) => {
    setCatGroupIsValid(true);
    setNewCategoryGroup(null);
    modalProps.onClose();
    onDismiss(isCreated);
  };

  const categoryGroupSelectHandler = (id: string, groupName: string) => {
    groupName && setCatGroupIsValid(true);
    const selectedGroup = CategoryDefinitions.find(d => d.name === groupName);

    setNewCategoryGroup({
      id: groupName,
      name: groupName,
      groupType: selectedGroup?.groupType || GroupType.Expense,
      categories: [
        {
          name: catName,
          cat_group: groupName,
          id: `${groupName}_${catName}`,
        },
      ],
    });
  };

  const createCategoryHandler = () => {
    if (!newCategoryGroup?.['name']) {
      setCatGroupIsValid(false);
    } else {
      onCreate(newCategoryGroup);
      modalCloseHandler(true);
    }
  };

  return (
    <Modal
      {...modalProps}
      title="Create new category"
      onClose={() => modalCloseHandler(false)}
    >
      <View style={{ gap: 16, marginBottom: '20px' }}>
        <View style={{ alignItems: 'center', flexDirection: 'row', gap: 5 }}>
          Category name:
          <Text style={{ fontWeight: 'bold' }}>{catName}</Text>
        </View>
        <View style={{ alignItems: 'center', flexDirection: 'row', gap: 5 }}>
          Category group:
          <CategoryGroupsAutocomplete
            categoryGroups={categoryGroupsFiltered}
            embedded={false}
            value={null}
            onSelect={categoryGroupSelectHandler}
            inputProps={{
              placeholder: 'Select group',
            }}
            tableBehavior={false}
          />
        </View>
        {!catGroupIsValid && <FormError>Category group is required</FormError>}
      </View>
      <View style={{ flexDirection: 'row', gap: 5 }}>
        <Button type="bare" style={{ flex: 1 }} onClick={() => modalCloseHandler(false)}>
          Cancel
        </Button>
        <ButtonWithLoading
          type="primary"
          style={{ flex: 1 }}
          onClick={createCategoryHandler}
        >
          Create category
        </ButtonWithLoading>
      </View>
    </Modal>
  );
};

export default CreateCategory;
